import * as Sentry from "@sentry/react";
import { PostHogProvider } from "posthog-js/react";
// import "./wdyr"; // <--- first import if you want to activate why did you render package, then add (e.g ProcessStepSection.whyDidYouRender = true;) to the component
import React from "react";
import ReactDOM from "react-dom/client";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";

import "./app/i18n/i18n";
import "./index.css";
import { colors } from "#app/theme/v1/colors";

import packageJson from "../package.json";
import App from "./App";

const isDevEnv =
  import.meta.env.VITE_BASE_API_URL.includes("localhost") ||
  import.meta.env.VITE_BASE_API_URL.includes("127.0.0.1");

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_ENV,
  release: packageJson.version,
  enabled: !isDevEnv,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration({
      maskAllText: false,
      maskAllInputs: false,
    }),
    Sentry.browserTracingIntegration(),
    Sentry.browserProfilingIntegration(),
    Sentry.feedbackIntegration({
      autoInject: false,
      showName: false,
      showEmail: false,
      showBranding: false,
      triggerLabel: "Nous contacter",
      formTitle: "Nous contacter",
      submitButtonLabel: "Envoyer",
      cancelButtonLabel: "Annuler",
      confirmButtonLabel: "Confirmer",
      addScreenshotButtonLabel: "Ajouter une capture d'écran",
      removeScreenshotButtonLabel: "Supprimer la capture d'écran",
      isRequiredLabel: "(requise)",
      messagePlaceholder: "Un problème ? Décrivez ce que vous vous attendiez à voir",
      themeLight: {
        accentBackground: colors.blue[5],
      },
      successMessageText: "Merci pour votre retour !",
    }),
  ],
  beforeSend(event, hint) {
    const exception = hint.originalException;
    if (exception !== null && exception !== undefined && !(exception instanceof Error)) {
      const message =
        typeof exception === "string"
          ? exception
          : (() => {
              try {
                return JSON.stringify(exception);
              } catch {
                return exception;
              }
            })();
      if (message === "{}") {
        return null;
      }
      event.exception?.values?.forEach((v) => {
        v.value = message;
      });
    }
    return event;
  },
  // Performance Monitoring
  tracesSampleRate: isDevEnv ? 1.0 : 0.2, // Capture 100% of the transactions, reduce in production!
  profilesSampleRate: 1.0,
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

const options = {
  api_host: import.meta.env.VITE_POSTHOG_HOST,
  session_recording: {
    maskAllInputs: false,
    maskInputOptions: {
      password: true,
    },
  },
  logs: {
    serviceName: "fabera-web",
    environment: import.meta.env.VITE_ENV,
    serviceVersion: packageJson.version,
  },
};
const POSTHOG_API_KEY = import.meta.env.VITE_POSTHOG_KEY;
const isPostHogEnabled = options.api_host && POSTHOG_API_KEY;

const root = ReactDOM.createRoot(document.getElementById("root"));

root.render(
  <React.StrictMode>
    {isPostHogEnabled ? (
      <PostHogProvider apiKey={POSTHOG_API_KEY} options={options}>
        <App />
      </PostHogProvider>
    ) : (
      <App />
    )}
  </React.StrictMode>,
);
